import React, { useEffect, useRef } from "react";
import "./MemeCard.css";

/* ── helpers ── */
function colorWithAlpha(color, alpha) {
  if (color.startsWith("#")) return color + Math.round(alpha * 255).toString(16).padStart(2, "0");
  if (color.startsWith("hsl(")) return color.replace("hsl(", "hsla(").replace(")", `, ${alpha})`);
  return color;
}

function truncate(str, max) {
  if (!str) return "?";
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
}

function generateCaption(stats, names) {
  const { winner, mostBetrayed, biggestVillain, legendaryPlayer, totalCaptures } = stats;
  const wName = names[winner] || "Someone";

  if (
    biggestVillain && mostBetrayed && biggestVillain.count >= 2 &&
    biggestVillain.playerId !== mostBetrayed.playerId
  ) {
    return `"${names[biggestVillain.playerId]} sent ${names[mostBetrayed.playerId]} home ${biggestVillain.count}x. Savage! 😤"`;
  }
  if (legendaryPlayer !== null && legendaryPlayer !== undefined) {
    return `"${names[legendaryPlayer]} rolled three 6s in a row. Blessed by the dice gods. 🎲"`;
  }
  if (totalCaptures === 0) {
    return '"Zero betrayals this game. Either too kind or too slow. 🤔"';
  }
  if (totalCaptures >= 8) {
    return `"${totalCaptures} betrayals in one game. Call the referee! 🔥"`;
  }
  if (biggestVillain && biggestVillain.count >= 3) {
    return `"${names[biggestVillain.playerId]} ate ${biggestVillain.count} pieces. Absolutely ruthless. 😈"`;
  }
  return `"${wName} dominated from start to finish. No contest. 👑"`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line.trim(), x, cy);
      line = word + " ";
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line.trim()) ctx.fillText(line.trim(), x, cy);
  return cy;
}

/* ── canvas drawing ── */
function drawCard(canvas, stats, names, colors, teamWinner) {
  const W = 420;
  const H = 660;
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0,   "#0d0d1a");
  bg.addColorStop(0.6, "#13132b");
  bg.addColorStop(1,   "#0a0a18");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Subtle grid
  ctx.strokeStyle = "rgba(255,255,255,0.025)";
  ctx.lineWidth = 1;
  for (let gx = 0; gx < W; gx += 24) {
    ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke();
  }
  for (let gy = 0; gy < H; gy += 24) {
    ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke();
  }

  // Gold border
  ctx.strokeStyle = "rgba(250,204,21,0.75)";
  ctx.lineWidth = 2.5;
  roundRect(ctx, 10, 10, W - 20, H - 20, 14);
  ctx.stroke();
  ctx.strokeStyle = "rgba(250,204,21,0.12)";
  ctx.lineWidth = 1;
  roundRect(ctx, 15, 15, W - 30, H - 30, 11);
  ctx.stroke();

  // ── Header ──
  ctx.textAlign = "center";
  ctx.fillStyle = "#facc15";
  ctx.font = "bold 24px system-ui, -apple-system, sans-serif";
  ctx.fillText("🎲 LUDO MANIA", W / 2, 56);

  ctx.fillStyle = "rgba(255,255,255,0.3)";
  ctx.font = "500 10px system-ui";
  try { ctx.letterSpacing = "3px"; } catch (_) {}
  ctx.fillText("G A M E   R E C A P", W / 2, 78);
  try { ctx.letterSpacing = "0px"; } catch (_) {}

  // Header divider
  const hDiv = ctx.createLinearGradient(30, 0, W - 30, 0);
  hDiv.addColorStop(0, "transparent");
  hDiv.addColorStop(0.4, "rgba(250,204,21,0.55)");
  hDiv.addColorStop(0.6, "rgba(250,204,21,0.55)");
  hDiv.addColorStop(1, "transparent");
  ctx.strokeStyle = hDiv;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(30, 90); ctx.lineTo(W - 30, 90); ctx.stroke();

  // ── Winner ──
  const winnerDisplay = teamWinner
    ? `Team ${teamWinner}`
    : truncate(names[stats.winner] ?? "?", 14);
  const winnerColor = teamWinner
    ? (teamWinner === "A" ? "#22c55e" : "#f59e0b")
    : (colors?.[stats.winner] || "#facc15");

  ctx.fillStyle = "rgba(250,204,21,0.6)";
  ctx.font = "700 10px system-ui";
  try { ctx.letterSpacing = "2px"; } catch (_) {}
  ctx.fillText("🏆  W I N N E R", W / 2, 118);
  try { ctx.letterSpacing = "0px"; } catch (_) {}

  // Winner glow
  const wGlow = ctx.createRadialGradient(W / 2, 150, 0, W / 2, 150, 90);
  wGlow.addColorStop(0, colorWithAlpha(winnerColor, 0.157));
  wGlow.addColorStop(1, "transparent");
  ctx.fillStyle = wGlow;
  ctx.fillRect(0, 108, W, 80);

  ctx.fillStyle = winnerColor;
  ctx.font = "bold 36px system-ui";
  ctx.fillText(winnerDisplay, W / 2, 160);

  ctx.beginPath();
  ctx.arc(W / 2, 177, 5, 0, Math.PI * 2);
  ctx.fillStyle = winnerColor;
  ctx.fill();

  // ── Stats divider ──
  ctx.strokeStyle = "rgba(255,255,255,0.07)";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(30, 194); ctx.lineTo(W - 30, 194); ctx.stroke();

  // ── Stats rows ──
  const rows = [
    {
      icon: "💀",
      label: "Most betrayed",
      value: stats.mostBetrayed
        ? `${truncate(names[stats.mostBetrayed.playerId], 11)} · ${stats.mostBetrayed.count}×`
        : "Nobody!",
    },
    {
      icon: "😈",
      label: "Biggest villain",
      value: stats.biggestVillain
        ? `${truncate(names[stats.biggestVillain.playerId], 11)} · ${stats.biggestVillain.count} eats`
        : "Nobody!",
    },
    {
      icon: "🎲",
      label: "Legendary roll",
      value: (stats.legendaryPlayer !== null && stats.legendaryPlayer !== undefined)
        ? truncate(names[stats.legendaryPlayer], 13)
        : "— nobody",
    },
    {
      icon: "⏱️",
      label: "Game time",
      value: stats.gameTime,
    },
    {
      icon: "💥",
      label: "Total betrayals",
      value: String(stats.totalCaptures),
    },
  ];

  let sy = 216;
  rows.forEach((row, i) => {
    if (i % 2 === 0) {
      ctx.fillStyle = "rgba(255,255,255,0.025)";
      ctx.fillRect(24, sy - 14, W - 48, 36);
    }
    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.38)";
    ctx.font = "400 11.5px system-ui";
    ctx.fillText(`${row.icon}  ${row.label}`, 38, sy + 8);

    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "600 13px system-ui";
    ctx.fillText(row.value, W - 38, sy + 8);

    sy += 40;
  });

  // ── Caption ──
  const capY = sy + 10;

  const capDiv = ctx.createLinearGradient(30, 0, W - 30, 0);
  capDiv.addColorStop(0, "transparent");
  capDiv.addColorStop(0.4, "rgba(250,204,21,0.22)");
  capDiv.addColorStop(0.6, "rgba(250,204,21,0.22)");
  capDiv.addColorStop(1, "transparent");
  ctx.strokeStyle = capDiv;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(30, capY); ctx.lineTo(W - 30, capY); ctx.stroke();

  ctx.fillStyle = "rgba(255,255,255,0.62)";
  ctx.font = "italic 13px system-ui";
  ctx.textAlign = "center";
  wrapText(ctx, generateCaption(stats, names), W / 2, capY + 26, W - 80, 22);

  // ── Footer ──
  ctx.strokeStyle = "rgba(250,204,21,0.18)";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(40, H - 42); ctx.lineTo(W - 40, H - 42); ctx.stroke();

  ctx.fillStyle = "rgba(250,204,21,0.45)";
  ctx.font = "500 11px system-ui";
  try { ctx.letterSpacing = "1px"; } catch (_) {}
  ctx.textAlign = "center";
  ctx.fillText("www.ludomania.live", W / 2, H - 24);
  try { ctx.letterSpacing = "0px"; } catch (_) {}
}

/* ── component ── */
export default function MemeCard({
  stats, playerNames, playerColors, teamWinner,
  onPlayAgain, onExitHome,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !stats) return;
    drawCard(canvasRef.current, stats, playerNames, playerColors, teamWinner);
  }, [stats, playerNames, playerColors, teamWinner]);

  function handleDownload() {
    const c = canvasRef.current;
    if (!c) return;
    const a = document.createElement("a");
    a.download = "ludo-mania-recap.png";
    a.href = c.toDataURL("image/png");
    a.click();
  }

  async function handleShare() {
    const c = canvasRef.current;
    if (!c) return;
    try {
      const blob = await new Promise((res) => c.toBlob(res, "image/png"));
      const file = new File([blob], "ludo-mania-recap.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Ludo Mania Recap 🎲" });
        return;
      }
    } catch (_) {}
    handleDownload();
  }

  return (
    <div className="memeCardOverlay">
      <div className="memeCardModal">
        <canvas ref={canvasRef} className="memeCanvas" />

        {/* Share row */}
        <div className="memeCardActions memeCardShareRow">
          <button className="memeBtn memeBtnSave"  onClick={handleDownload}>💾 Save</button>
          <button className="memeBtn memeBtnShare" onClick={handleShare}>📸 Share Story</button>
        </div>

        {/* Navigation row */}
        <div className="memeCardActions memeCardNavRow">
          {onPlayAgain && (
            <button className="memeBtn memeBtnPlayAgain" onClick={onPlayAgain}>🔄 Play Again</button>
          )}
          <button className="memeBtn memeBtnExit" onClick={onExitHome}>🏠 Exit to Home</button>
        </div>
      </div>
    </div>
  );
}
